export type ProgressContainerProps = {
   active: boolean
   displayMode: 'elapsed' | 'remaining'
   fatBurnLabel: string
   fatBurnTitle: string
   onToggleDisplayMode: () => void
   progressBackground: string
   progressLabel: string
   syncLoading: boolean
   timerLabel: string
   toggleLabel: string
   /** Prefix for the element ids, so a second ring (sleep) can share the page. */
   idPrefix?: string
}

/**
 * The circular fast-progress ring container; toggles display mode on click/keyboard.
 *
 * @param props - The progress display props.
 * @returns The container element.
 */
const ProgressContainer = (props: ProgressContainerProps) => {
   /**
    * Returns whether the display mode can be toggled (active and not loading).
    *
    * @returns True when toggling is allowed.
    */
   const canToggleDisplayMode = () => props.active && !props.syncLoading
   /**
    * Returns an element id with the optional prefix applied.
    *
    * @param name - The unprefixed id.
    * @returns The id.
    */
   const elementId = (name: string) => `${props.idPrefix ?? ''}${name}`

   /**
    * Toggles the timer display mode when allowed.
    */
   const toggleDisplayMode = () => {
      if (canToggleDisplayMode()) {
         props.onToggleDisplayMode()
      }
   }

   return (
      <div
         id={elementId('progress-container')}
         class="relative mt-6 mx-4 mb-4 flex h-72 w-72 items-center justify-center max-[400px]:h-60 max-[400px]:w-60 lg:mx-4 lg:mb-3 lg:h-72 lg:w-72 cursor-pointer rounded-2xl focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-strong"
         tabindex="0"
         role="button"
         data-sync-loading={props.syncLoading ? 'true' : undefined}
         aria-busy={props.syncLoading ? 'true' : undefined}
         onClick={toggleDisplayMode}
         onKeyDown={event => {
            if (canToggleDisplayMode() && (event.key === 'Enter' || event.key === ' ')) {
               event.preventDefault()
               toggleDisplayMode()
            }
         }}
         aria-pressed={props.displayMode === 'remaining' ? 'true' : 'false'}
         aria-label={props.toggleLabel}
      >
         <div
            class="progress-circle w-full h-full rounded-full"
            style={{ background: props.progressBackground }}
         ></div>
         <div class="progress-circle-inner absolute w-64 h-64 rounded-full max-[400px]:w-52 max-[400px]:h-52 lg:w-64 lg:h-64"></div>
         <div
            id={elementId('timer-display')}
            class="absolute flex max-w-[13rem] flex-col items-center justify-center gap-1 text-center select-none max-[400px]:max-w-[10.5rem]"
         >
            <span
               id={elementId('progress-percentage')}
               data-sync-loading={props.syncLoading ? 'true' : undefined}
               class="text-lg text-ink-muted"
            >
               {props.progressLabel}
            </span>
            <span
               id={elementId('elapsed-time')}
               data-sync-loading={props.syncLoading ? 'true' : undefined}
               class="text-4xl font-mono font-bold tracking-wider max-[400px]:text-3xl"
            >
               {props.timerLabel}
            </span>
            <span
               id={elementId('fat-burn-summary')}
               data-sync-loading={props.syncLoading ? 'true' : undefined}
               class="mt-1 flex flex-col items-center text-sm text-ink-soft max-[400px]:text-xs"
            >
               <span>{props.fatBurnTitle}</span>
               <span>{props.fatBurnLabel}</span>
            </span>
         </div>
      </div>
   )
}

export default ProgressContainer
